<template>
  <div :data-testid="testId" class="flex flex-col gap-2">
    <!-- No toque não existe hover: o motivo da trava (RN-07, "após o fim do plantão")
         que no desktop é tooltip vira texto visível aqui. -->
    <div
      v-if="hintLines.length"
      :data-testid="`${testId}-hint`"
      class="flex flex-col gap-0.5 text-left text-xs text-gray-600"
    >
      <p v-for="line in hintLines" :key="line">{{ line }}</p>
    </div>

    <!-- `border-solid` explícito: o reset do Vuetify zera `border-style` em <button>
         (mesma nota do menu flutuante do stationDetailsModal). -->
    <div :data-testid="`${testId}-buttons`" class="flex items-center gap-2">
      <button
        v-if="primary"
        type="button"
        :data-testid="`${testId}-primary`"
        :disabled="primary.disabled"
        class="h-11 min-w-0 flex-1 rounded-lg bg-primary30 px-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
        @click="primary.handler()"
      >
        {{ primary.label }}
      </button>
      <button
        v-if="secondary"
        type="button"
        :data-testid="`${testId}-secondary`"
        :disabled="secondary.disabled"
        class="h-11 min-w-0 flex-1 rounded-lg border-2 border-solid border-primary30 bg-white px-3 text-sm font-bold text-primary30 disabled:cursor-not-allowed disabled:opacity-50"
        @click="secondary.handler()"
      >
        {{ secondary.label }}
      </button>
      <!-- Sem primária nem secundária (ex.: smart allocation sem permissão de alocar
           e escala sem Registro de Atividade — mesma regra do menu do desktop), o
           "⋯" sozinho ocuparia 44px encostado à esquerda e o dock pareceria
           quebrado: aí ele ocupa a largura e mostra "Mais ações" em texto. -->
      <button
        v-if="sheetItems.length"
        type="button"
        :data-testid="`${testId}-more`"
        :aria-label="isMoreAlone ? undefined : MORE_LABEL"
        class="flex h-11 items-center justify-center gap-2 rounded-lg border-2 border-solid border-primary30 bg-white text-primary30"
        :class="isMoreAlone ? 'min-w-0 flex-1 px-3 text-sm font-bold' : 'w-11 shrink-0'"
        @click="isSheetOpen = true"
      >
        <SVGIcon :path="mdiDotsHorizontal" class="h-5 w-5 shrink-0" aria-hidden="true" />
        <span v-if="isMoreAlone">{{ MORE_LABEL }}</span>
      </button>
    </div>

    <ActionSheet
      v-model="isSheetOpen"
      title="Ações do plantão"
      :items="sheetItems"
      test-id="station-details-actions"
      @select="onSelect"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Dock fixo do Detalhes do Plantão no celular (spec 2026-09-30 §5 R6): a primária
 * ("Alterar/Alocar/Identificar médico", ou "Editar plantão" sem smart allocation),
 * "Enviar registro" em SEGUNDO (Q35) e "⋯" com o resto num ActionSheet. Tudo sai da
 * lista do `useStationDetailActions` — as condições são as mesmas do desktop.
 */
import { computed, ref } from 'vue'
import { mdiDotsHorizontal } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import ActionSheet from '@/components/designSystem/ActionSheet/ActionSheet.vue'
import type { ActionSheetItem } from '@/components/designSystem/ActionSheet/types'
import type {
  StationDetailAction,
  StationDetailActionId
} from '../composables/useStationDetailActions'

const props = withDefaults(defineProps<{ actions: StationDetailAction[]; testId?: string }>(), {
  testId: 'station-details-dock'
})

/** Ordem do mockup R6 (Editar, Histórico, …) com a ação destrutiva por último. */
const SHEET_ORDER: StationDetailActionId[] = [
  'edit',
  'history',
  'exceptionality',
  'check-attempts',
  'remove'
]

const MORE_LABEL = 'Mais ações'

const isSheetOpen = ref(false)

const primary = computed(() => props.actions.find((a) => a.placement === 'primary'))
const secondary = computed(() => props.actions.find((a) => a.placement === 'secondary'))

/** "⋯" é o único botão do dock: vira botão de largura cheia com rótulo. */
const isMoreAlone = computed(() => !primary.value && !secondary.value)

/**
 * Motivos distintos das ações do dock travadas, uma linha por motivo. Com um motivo
 * só (ou o mesmo nas duas), a linha é o motivo puro; com dois diferentes, cada linha
 * leva o rótulo do botão para dizer qual trava é de qual.
 */
const hintLines = computed(() => {
  const locked = [primary.value, secondary.value].filter(
    (a): a is StationDetailAction => !!a?.disabled && !!a.disabledReason
  )
  const reasons = [...new Set(locked.map((a) => a.disabledReason as string))]
  if (reasons.length <= 1) return reasons
  return locked.map((a) => `${a.label}: ${a.disabledReason}`)
})

/** Posição no sheet; id fora da lista conhecida vai para o fim. */
const sheetRank = (id: StationDetailActionId) => {
  const index = SHEET_ORDER.indexOf(id)
  return index === -1 ? Infinity : index
}

const sheetActions = computed(() =>
  props.actions
    .filter((a) => a.placement === 'overflow' || a.placement === 'standalone')
    .sort((a, b) => sheetRank(a.id) - sheetRank(b.id))
)

const sheetItems = computed<ActionSheetItem[]>(() =>
  sheetActions.value.map((a) => ({
    id: a.id,
    label: a.label,
    icon: a.icon,
    danger: a.danger,
    disabled: a.disabled,
    disabledReason: a.disabledReason,
    description: a.disabled && a.disabledReason ? a.disabledReason : a.description
  }))
)

function onSelect(id: string) {
  const selected = sheetActions.value.find((a) => a.id === id)
  if (!selected || selected.disabled) return
  selected.handler()
}
</script>
